import * as React from "react"

import { Skeleton } from "@ibada/ui"

import { useTranslations } from "@/i18n"
import { useFinanceFeatureQuery } from "@/features/finance/services/finance-query"
import { FinanceOverviewCardId } from "@/features/finance/model/finance-normalized-overview"
import { findOverviewCard } from "@/features/finance/lib/finance-overview-integration"
import type { FinanceOverviewQuery } from "@/features/finance/model/finance-types"
import { useWidgetMenu } from "@ibada/dashboard-engine"

import { WidgetBoundary } from "@/features/finance/overview/components/widget-boundary"
import {
  useFinanceOverviewPeriod,
  useFinanceOverviewScope,
} from "@/features/finance/overview/context/finance-overview-context"

/**
 * How much bookkeeping is actually happening church-wide: postings made, and activities currently running.
 *
 * Money totals say what was recorded; this says how much recording went on. The two answer different questions,
 * and the gap between them is informative — a large sum arriving in very few postings usually means one big
 * transfer rather than a busy month, and a period with almost no postings across the whole church is a sign the
 * books are behind rather than that giving stopped.
 *
 * Both figures are server-computed cards; nothing is counted here.
 */
export function ChurchActivityVolumeWidget() {
  const t = useTranslations("finance.overview")
  const scope = useFinanceOverviewScope()
  const period = useFinanceOverviewPeriod()
  const { useFinanceOverviewQuery } = useFinanceFeatureQuery()

  const query = useFinanceOverviewQuery(
    scope.workspaceId && period.from && period.to
      ? ({
          workspaceId: scope.workspaceId,
          context: scope.overviewContext,
          dateFrom: period.from,
          dateTo: period.to,
        } satisfies FinanceOverviewQuery)
      : null,
  )

  const data = React.useMemo(() => {
    if (!query.data) return undefined
    const cards = query.data.cards ?? []
    const readCount = (id: FinanceOverviewCardId) => {
      const value = findOverviewCard(cards, id)?.value
      const parsed = typeof value === "number" ? value : Number(value)
      return Number.isFinite(parsed) ? parsed : 0
    }
    return {
      transactions: readCount(FinanceOverviewCardId.TransactionCount),
      activities: readCount(FinanceOverviewCardId.ActiveActivitiesCount),
    }
  }, [query.data])

  useWidgetMenu({ refresh: () => void query.refetch() })

  return (
    <WidgetBoundary
      isLoading={query.isLoading}
      isError={query.isError}
      isFetching={query.isFetching}
      data={data}
      skeleton={<VolumeSkeleton />}
      errorTitle={t("widgets.churchActivityVolume.error")}
      onRetry={() => void query.refetch()}
      retryLabel={t("widgets.common.retry")}
    >
      {(d) => (
        <div className='space-y-3'>
          <div className='space-y-1'>
            <p className='text-foreground text-2xl font-semibold tabular-nums tracking-tight'>
              {d.transactions.toLocaleString()}
            </p>
            <p className='text-muted-foreground text-xs'>{t("widgets.churchActivityVolume.transactions")}</p>
          </div>
          <div className='border-border/60 border-t pt-2'>
            <p className='text-foreground text-sm font-medium tabular-nums'>{d.activities.toLocaleString()}</p>
            <p className='text-muted-foreground text-xs'>{t("widgets.churchActivityVolume.activities")}</p>
          </div>
        </div>
      )}
    </WidgetBoundary>
  )
}

function VolumeSkeleton() {
  return (
    <div className='space-y-3'>
      <div className='space-y-1'>
        <Skeleton className='h-7 w-20' />
        <Skeleton className='h-3 w-28' />
      </div>
      <div className='space-y-1 border-t pt-2'>
        <Skeleton className='h-4 w-12' />
        <Skeleton className='h-3 w-24' />
      </div>
    </div>
  )
}
